import { useMemo, useRef, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import {
  Button,
  cn,
  Icon,
  Sheet,
  SheetBody,
  SheetClose,
  SheetContent,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from "@nexus/ui";
import type { BigQueryUsageExecution } from "@/features/bigquery-usage/api/bigquery-usage.api";
import { FailurePanel } from "@/components/FailurePanel";
import {
  EVENT_DETAIL_ROW_GRID,
  EventDetailSection,
} from "@/features/events/components/shared/EventDetailSection";
import {
  formatEventClockTime,
  formatEventDate,
  formatPreciseDurationMs,
} from "@/features/events/lib/event-timing";
import { TenantMark } from "@/features/llm-usage/components/TenantMark";
import { formatMicrosAsUsd } from "@/features/llm-usage/lib/money";
import { useCopyToClipboard } from "@/hooks/useCopyToClipboard";
import { formatBytesCompact, formatBytesExact } from "../lib/bytes";
import { shortFingerprint } from "../lib/fingerprint-display";
import { formatNormalizedSql } from "../lib/sql-format";
import { splitSqlNote } from "../lib/sql-note";
import { SqlLine } from "./SqlLine";

export interface BigQueryExecutionDrawerProps {
  /** The row the URL points at, resolved against the pages already fetched. Null closes the drawer. */
  execution: BigQueryUsageExecution | null;
  onClose: () => void;
  /**
   * Opens the Events tab on this correlation id. The admin route owns that tab's
   * search shape, so the jump is reported upward rather than built here.
   */
  onOpenCorrelation?: (correlationId: string) => void;
}

const EMPTY_VALUE = "—";
const COPIED_RESET_MS = 2_000;
/** The size the list's own tenant cell draws its mark at, one click away. */
const TENANT_MARK_SIZE = 16;

function CopyFieldButton({ value, label }: { value: string; label: string }) {
  const { t } = useTranslation();
  const { copied, copy } = useCopyToClipboard({ resetMs: COPIED_RESET_MS });

  return (
    <Button
      variant="ghost"
      size="icon-xs"
      className="size-5 shrink-0 rounded-sm"
      onClick={() => void copy(value)}
      aria-label={
        copied
          ? t("admin.bigQueryUsage.executionDrawer.copied", "Copied")
          : t("admin.bigQueryUsage.executionDrawer.copyField", "Copy {{field}}", { field: label })
      }
    >
      <Icon
        name={copied ? "check" : "copy"}
        size="xs"
        colorVar={copied ? "--success" : undefined}
        aria-hidden
      />
    </Button>
  );
}

/**
 * The normalized template, laid out a clause per line. The preview is stored as
 * a single collapsed line capped at 200 characters, which reads as prose in a
 * block this narrow.
 *
 * The author's own note leads the block on its own line, back behind the `--`
 * the collapse left it without: the whitespace run that ended the comment is
 * gone, so the formatter below would otherwise lay the SQL out as a continuation
 * of the prose.
 */
function SqlStatement({ sql }: { sql: string }) {
  const { t } = useTranslation();
  const { note, statement } = useMemo(() => splitSqlNote(sql), [sql]);
  const lines = useMemo(() => formatNormalizedSql(statement), [statement]);

  if (lines.length === 0 && note === null) {
    return (
      <p className="text-xs text-muted-foreground">
        {t("admin.bigQueryUsage.executionDrawer.noStatement", "No statement was recorded.")}
      </p>
    );
  }

  return (
    <pre className="m-0 max-h-64 overflow-auto whitespace-pre-wrap rounded-md border border-border bg-muted px-3 py-2 font-mono text-[11px] leading-[1.6]">
      {note === null ? null : <span className="block text-muted-foreground">{`-- ${note}`}</span>}
      {lines.map((line, index) => (
        <SqlLine key={index} line={line} className="block" />
      ))}
    </pre>
  );
}

/**
 * Which tenant the query ran for — the same reading as the list's `TenantCell`,
 * so the drawer a row opens names its tenant the way the row did.
 *
 * The id stays on the row rather than on a hover: this is where an operator goes
 * to copy it, and a copy button whose payload is nowhere on screen is a trap. A
 * tenant deleted since the query ran has no name left, so the id is all the row
 * can offer — initials derived from a cuid would name nothing.
 */
function TenantValue({ execution }: { execution: BigQueryUsageExecution }) {
  const { t } = useTranslation();

  if (execution.tenantKind === "platform") {
    return (
      <span className="flex items-center gap-1.5">
        <Icon name="shield" size="xs" aria-hidden />
        {t("admin.bigQueryUsage.tenantTable.platform", "Platform")}
      </span>
    );
  }

  if (execution.tenantKind === "unattributed") {
    return (
      <span className="flex items-center gap-1.5 text-muted-foreground">
        <Icon name="info" size="xs" aria-hidden />
        {t("admin.bigQueryUsage.tenantTable.unattributed", "Unattributed")}
      </span>
    );
  }

  if (execution.tenantName == null) {
    return (
      <span className="break-all font-mono text-[11px]">{execution.tenantId ?? EMPTY_VALUE}</span>
    );
  }

  return (
    <span className="flex flex-col gap-0.5">
      <span className="flex items-center gap-1.5">
        <TenantMark
          name={execution.tenantName}
          logoUrl={execution.logoUrl}
          brandColor={execution.brandColor}
          size={TENANT_MARK_SIZE}
        />
        {execution.tenantName}
      </span>
      {execution.tenantId === null ? null : (
        <span className="break-all font-mono text-[11px] text-muted-foreground">
          {execution.tenantId}
        </span>
      )}
    </span>
  );
}

function DetailRow({
  label,
  value,
  display,
  mono = false,
  copyable = false,
  action,
}: {
  label: string;
  value: string;
  /**
   * Drawn in place of `value` when the row has more to show than a string —
   * `value` stays what the copy button copies, so the two cannot drift.
   */
  display?: ReactNode;
  mono?: boolean;
  copyable?: boolean;
  action?: ReactNode;
}) {
  const isEmpty = value === EMPTY_VALUE;

  return (
    <div className={EVENT_DETAIL_ROW_GRID}>
      <dt className="text-xs leading-5 text-muted-foreground">{label}</dt>
      <dd
        className={cn(
          "leading-5",
          mono ? "break-all font-mono text-[11px]" : "text-xs",
          isEmpty && "text-muted-foreground",
        )}
      >
        {display ?? value}
      </dd>
      <dd className="flex items-center gap-1">
        {copyable && !isEmpty ? <CopyFieldButton value={value} label={label} /> : null}
        {action}
      </dd>
    </div>
  );
}

/**
 * One execution in full: what it cost, what it ran, and the two ids that make it
 * chaseable outside this tab — the BigQuery job id for Google's own record of
 * the job, and the correlation id for the Nexus request that caused it.
 *
 * The statement shown is the normalized template the fingerprint was taken over,
 * never the text that ran. Nexus does not store executed SQL, because a large
 * call-site family interpolates user-typed free text into it.
 */
export function BigQueryExecutionDrawer({
  execution,
  onClose,
  onOpenCorrelation,
}: BigQueryExecutionDrawerProps) {
  const { t, i18n } = useTranslation();
  const locale = i18n.language;
  const contentRef = useRef<HTMLDivElement>(null);

  if (!execution) return null;

  const { correlationId } = execution;
  const ranAt = [
    formatEventDate(execution.createdAt, locale),
    formatEventClockTime(execution.createdAt, locale),
  ]
    .filter(Boolean)
    .join(" · ");

  const tenantValue =
    execution.tenantKind === "platform"
      ? t("admin.bigQueryUsage.tenantTable.platform", "Platform")
      : execution.tenantKind === "unattributed"
        ? t("admin.bigQueryUsage.tenantTable.unattributed", "Unattributed")
        : (execution.tenantId ?? EMPTY_VALUE);

  const cacheValue = execution.cacheHit
    ? t("admin.bigQueryUsage.executionsTable.cached", "Cached")
    : t("admin.bigQueryUsage.executionsTable.fresh", "Fresh");

  return (
    <Sheet open onOpenChange={(open) => !open && onClose()}>
      {/* The first tabbable child is a copy button, and letting Radix autofocus
          it opens the drawer with a focus ring on an icon. Park focus on the
          panel so reading order still starts at the top. */}
      <SheetContent
        ref={contentRef}
        aria-describedby={undefined}
        className="focus:outline-none"
        onOpenAutoFocus={(focusEvent) => {
          focusEvent.preventDefault();
          contentRef.current?.focus();
        }}
      >
        <SheetHeader className="gap-0 pr-12">
          <SheetTitle>{execution.feature}</SheetTitle>
          <div className="mt-1 font-mono text-[10px] uppercase tracking-wide text-muted-foreground">
            {ranAt}
          </div>
        </SheetHeader>

        <SheetBody className="flex flex-col gap-6">
          {/* No code of its own: the reason BigQuery gives is the whole message. */}
          <FailurePanel key={`${execution.id}-error`} message={execution.errorReason} />

          <EventDetailSection label={t("admin.bigQueryUsage.executionDrawer.identity", "Identity")}>
            <dl className="flex flex-col gap-1.5">
              <DetailRow
                label={t("admin.bigQueryUsage.executionDrawer.correlationId", "Correlation ID")}
                value={correlationId ?? EMPTY_VALUE}
                mono
                copyable
                action={
                  correlationId && onOpenCorrelation ? (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-6 shrink-0 rounded-sm px-1.5 text-[11px]"
                      onClick={() => onOpenCorrelation(correlationId)}
                      aria-label={t(
                        "admin.bigQueryUsage.executionDrawer.openInEventsLabel",
                        "Open the Events tab filtered to {{correlationId}}",
                        { correlationId },
                      )}
                    >
                      <Icon name="arrow-right" size="xs" aria-hidden />
                      {t("admin.bigQueryUsage.executionDrawer.openInEvents", "Open in Events")}
                    </Button>
                  ) : null
                }
              />
              <DetailRow
                label={t("admin.bigQueryUsage.tenantTable.tenant", "Tenant")}
                value={tenantValue}
                display={<TenantValue execution={execution} />}
                copyable={execution.tenantKind === "tenant"}
              />
              <DetailRow
                label={t("admin.bigQueryUsage.executionDrawer.jobId", "Job ID")}
                value={execution.jobId ?? EMPTY_VALUE}
                mono
                copyable
              />
              <DetailRow
                label={t("admin.bigQueryUsage.executionDrawer.recordId", "Record ID")}
                value={execution.id}
                mono
                copyable
              />
            </dl>
            <p className="mt-2 text-[11px] text-muted-foreground">
              {t(
                "admin.bigQueryUsage.executionDrawer.jobIdNote",
                "Paste the job id into the BigQuery console to read Google's own record of the job.",
              )}
            </p>
          </EventDetailSection>

          <EventDetailSection label={t("admin.bigQueryUsage.executionDrawer.measures", "Measures")}>
            <dl className="flex flex-col gap-1.5">
              <DetailRow
                label={t("admin.bigQueryUsage.table.bytesBilled", "Bytes billed")}
                value={formatBytesExact(execution.bytesBilled, locale)}
              />
              {/* Directly under the bytes it is derived from, and priced by the
                  server so this figure and the breakdowns' cannot disagree. A
                  cache hit billed nothing and reads $0.00, which is the point. */}
              <DetailRow
                label={t("admin.bigQueryUsage.table.cost", "Cost")}
                value={
                  execution.costMicros === null
                    ? EMPTY_VALUE
                    : formatMicrosAsUsd(execution.costMicros, locale)
                }
                display={
                  execution.costMicros === null ? (
                    <span
                      title={t(
                        "admin.bigQueryUsage.table.costUnpricedTitle",
                        "No price per TB is set. Set it in Admin → Settings → Operations.",
                      )}
                    >
                      {EMPTY_VALUE}
                    </span>
                  ) : undefined
                }
              />
              <DetailRow
                label={t("admin.bigQueryUsage.executionDrawer.bytesProcessed", "Bytes processed")}
                value={formatBytesCompact(execution.bytesProcessed, locale)}
              />
              <DetailRow
                label={t("admin.bigQueryUsage.executionDrawer.slotTime", "Slot time")}
                value={formatPreciseDurationMs(execution.slotMs)}
              />
              <DetailRow
                label={t("admin.bigQueryUsage.executionsTable.duration", "Duration")}
                value={formatPreciseDurationMs(execution.durationMs)}
              />
              <DetailRow label={t("admin.bigQueryUsage.table.cache", "Cache")} value={cacheValue} />
              <DetailRow
                label={t("admin.bigQueryUsage.executionDrawer.attempts", "Attempts")}
                value={execution.attempts.toLocaleString(locale)}
              />
            </dl>
          </EventDetailSection>

          <EventDetailSection
            label={t("admin.bigQueryUsage.fingerprintTable.statement", "Statement")}
            aside={
              <span className="flex items-center gap-1.5 font-mono text-[11px] text-muted-foreground">
                {shortFingerprint(execution.sqlFingerprint)}
                <span>
                  {t("admin.bigQueryUsage.fingerprintTable.version", "v{{version}}", {
                    version: execution.fingerprintVersion,
                  })}
                </span>
              </span>
            }
          >
            <SqlStatement sql={execution.sqlPreview} />
            <p className="text-[11px] text-muted-foreground">
              {t(
                "admin.bigQueryUsage.executionDrawer.normalizedNote",
                "The normalized template this query was fingerprinted by, not the statement text that ran.",
              )}
            </p>
          </EventDetailSection>
        </SheetBody>

        <SheetFooter className="justify-end px-6">
          <SheetClose asChild>
            <Button variant="outline" size="sm" className="h-8 rounded-sm text-xs">
              {t("admin.bigQueryUsage.executionDrawer.close", "Close")}
            </Button>
          </SheetClose>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
}
